import { Box } from "@mui/material";
import { GridApi } from "ag-grid-community";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFieldMetadata } from "@platform/plan-grid-core";
import { Typography } from "@platform/design-system";

import ReviewList from "./ReviewList";

import type { CustomToolPanelProps } from "ag-grid-react";
import type { FieldCommentsIndex } from "@platform/plan-grid-core";

export interface ReviewPanelProps extends CustomToolPanelProps {
  title: string;
  api: GridApi;
}

export interface ReviewPanelItem {
  name: string;
  reviewItems: ReviewItem[];
  benefitKey: string;
}

export interface ReviewItem {
  identifier: string;
  cellIdentifier: string;
  comment?: string;
  fieldCommentId: string;
  fieldPath: string;
  planId: string;
}

export type ReviewListAction =
  | {
      type: "saveComment";
      payload: {
        comment: string;
        fieldCommentId: string;
        fieldPath: string;
        planId: string;
      };
    }
  | { type: "startEdit"; payload: { key: string } }
  | {
      type: "highlight";
      payload: { benefitKey: string; cellIdentifier: string };
    }
  | { type: "clearHighlights" }
  | { type: "remove"; payload: { fieldCommentId: string; planId: string } };

const buildReviewPanelData = (
  api: GridApi,
  commentsIndex: FieldCommentsIndex,
): ReviewPanelItem[] => {
  const benefitNameByKey = new Map<string, string>();
  const benefitOrder = new Map<string, number>();
  let rowIndex = 0;

  api.forEachNode((rowNode) => {
    const rowData = rowNode.data as
      | { benefit?: { label?: string }; benefitKey?: string }
      | undefined;

    if (!rowData?.benefitKey) {
      return;
    }

    benefitNameByKey.set(
      rowData.benefitKey,
      rowData.benefit?.label || rowData.benefitKey,
    );
    benefitOrder.set(rowData.benefitKey, rowIndex);
    rowIndex += 1;
  });

  const planLabelById = new Map<string, string>();
  const planOrder = new Map<string, number>();
  const columns = api.getColumns?.() ?? [];

  columns.forEach((column, index) => {
    const colDef = column.getColDef();
    const field = typeof colDef.field === "string" ? colDef.field : undefined;
    if (!field) {
      return;
    }

    const planId = field.split(".")[0];
    if (!planId || planLabelById.has(planId)) {
      return;
    }

    // For networked columns (field = "{planId}.{networkName}"), the leaf
    // headerTooltip includes the network suffix (e.g. "DEN-1-1.inNetwork").
    // Use the immediate parent column group's headerName instead, which holds
    // the plan option label in the 3-tier strategy and the policy period label
    // in the 2-tier networks-only strategy — both are cleaner plan identifiers.
    const isNetworked = field.includes(".");
    const parentHeaderName = isNetworked
      ? column.getParent()?.getColGroupDef()?.headerName
      : undefined;

    planLabelById.set(
      planId,
      parentHeaderName || colDef.headerTooltip || colDef.headerName || planId,
    );
    planOrder.set(planId, index);
  });

  const grouped = new Map<string, ReviewPanelItem>();

  Object.entries(commentsIndex).forEach(([planId, commentsByField]) => {
    Object.values(commentsByField).forEach((fieldComment) => {
      if (!fieldComment.needsReview || !fieldComment.fieldPath) {
        return;
      }

      const benefitKey = fieldComment.fieldPath;
      const existing = grouped.get(benefitKey);

      if (!existing) {
        grouped.set(benefitKey, {
          name: benefitNameByKey.get(benefitKey) || benefitKey,
          benefitKey,
          reviewItems: [],
        });
      }

      grouped.get(benefitKey)?.reviewItems.push({
        identifier: planLabelById.get(planId) || planId,
        cellIdentifier: planId,
        comment: fieldComment.comment ?? "",
        fieldCommentId: fieldComment.id,
        fieldPath: benefitKey,
        planId,
      });
    });
  });

  return Array.from(grouped.values())
    .sort(
      (left, right) =>
        (benefitOrder.get(left.benefitKey) ?? Number.MAX_SAFE_INTEGER) -
        (benefitOrder.get(right.benefitKey) ?? Number.MAX_SAFE_INTEGER),
    )
    .map((item) => ({
      ...item,
      reviewItems: [...item.reviewItems].sort(
        (left, right) =>
          (planOrder.get(left.planId) ?? Number.MAX_SAFE_INTEGER) -
          (planOrder.get(right.planId) ?? Number.MAX_SAFE_INTEGER),
      ),
    }));
};

export default function ReviewPanel(props: ReviewPanelProps): React.ReactNode {
  const { getFieldCommentsIndex, removeComment, updateComment } =
    useFieldMetadata();
  const [data, setData] = useState<ReviewPanelItem[]>([]);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const updateList = useCallback((): void => {
    setData(buildReviewPanelData(props.api, getFieldCommentsIndex()));
  }, [getFieldCommentsIndex, props.api]);

  const clearHighlights = (): void => {
    const els = Array.from(
      document.querySelectorAll(
        '.pd-hover-highlight, [data-hover-highlight="true"]',
      ),
    ) as HTMLElement[];
    els.forEach((el) => {
      el.classList.remove("pd-hover-highlight");
      el.removeAttribute("data-hover-highlight");
      try {
        // remove inline styles we may have applied
        el.style.outline = "";
        el.style.backgroundColor = "";
      } catch {
        // ignore
      }
    });
  };

  const highlightReviewItem = (
    benefitKey: string,
    cellIdentifier: string,
  ): void => {
    if (!benefitKey || !cellIdentifier) {
      return;
    }

    clearHighlights();

    try {
      props.api.forEachNode((rowNode) => {
        if (rowNode.data?.benefitKey === benefitKey) {
          try {
            if (typeof rowNode.rowIndex === "number") {
              props.api.ensureIndexVisible?.(rowNode.rowIndex);
            }
          } catch {
            // ignore
          }
        }
      });
    } catch {
      // ignore
    }

    const className = `${benefitKey}-${cellIdentifier}`;

    if (highlightTimeoutRef.current !== null) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }

    highlightTimeoutRef.current = setTimeout(() => {
      highlightTimeoutRef.current = null;
      try {
        const els = Array.from(
          document.querySelectorAll(".ag-cell"),
        ) as HTMLElement[];

        els.forEach((el) => {
          try {
            const matches = Array.from(el.classList).some(
              (candidate) =>
                candidate === className ||
                candidate.startsWith(`${className}.`),
            );

            if (!matches) {
              return;
            }

            el.classList.add("pd-hover-highlight");
            el.setAttribute("data-hover-highlight", "true");
          } catch {
            // ignore
          }
        });
      } catch {
        // ignore
      }
    }, 120);
  };

  useEffect(() => {
    updateList();
    props.api.addEventListener("modelUpdated", updateList);

    return () => {
      if (highlightTimeoutRef.current !== null) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
      if (!props.api.isDestroyed()) {
        props.api.removeEventListener("modelUpdated", updateList);
      }
    };
  }, [props.api, updateList]);

  const handleReviewListUpdate = (action: ReviewListAction): void => {
    switch (action.type) {
      case "saveComment": {
        const { comment, fieldCommentId, planId } = action.payload;
        if (!planId || !fieldCommentId) {
          break;
        }

        updateComment(planId, fieldCommentId, {
          comment: comment || "",
          needsReview: true,
        });
        break;
      }
      case "highlight": {
        const { benefitKey, cellIdentifier } = action.payload;
        if (benefitKey && cellIdentifier) {
          highlightReviewItem(benefitKey, cellIdentifier);
        }
        break;
      }
      case "clearHighlights": {
        clearHighlights();
        break;
      }
      case "startEdit": {
        // optional: keep external editing state in sync if desired
        // setEditingKey(action.payload?.key ?? null);
        break;
      }
      case "remove": {
        const { fieldCommentId, planId } = action.payload;
        if (!planId || !fieldCommentId) {
          break;
        }

        removeComment(planId, fieldCommentId);
        break;
      }
      default:
        break;
    }
  };

  return (
    <Box width="100%">
      <Box p={2}>
        <Typography variant="header-small">{props.title}</Typography>
      </Box>
      {data.length > 0 && (
        <ReviewList data={data} onUpdate={handleReviewListUpdate} />
      )}
    </Box>
  );
}
